import React from "react";
import { Grid, Typography } from "@mui/material";
import theme from "Theme/AppTheme";
import AcxCard from "components/UI/Cards/AcxCard";
import { SignalsDashboardType } from "../SignalsModels";
import { defaultShadowsForSignals } from "../SignalsUtils";

type Props = {
    dashboardType: SignalsDashboardType;
};

// Copy for each dashboard type. Only Eddy is enabled for now.
const variantCopy: Record<
    SignalsDashboardType,
    {
        enabled: boolean;
        title: string;
        subTitle1: string;
        subTitle2: string;
    } | null
> = {
    [SignalsDashboardType.Eddy]: {
        enabled: true,
        title: "The Eddy Effect",
        subTitle1:
            "On this page, explore the question: in what situations do my customers experience friction or feel stuck?",
        subTitle2:
            'An "Eddy" is when the customer\'s desired or expected experience is disrupted by an obstacle or barrier that causes the customer to feel “stuck” in a problem.',
    },
    [SignalsDashboardType.Trends]: {
        enabled: false,
        title: "Trends Highlights",
        subTitle1:
            "Trends allow you to see key insights over time, including indications of how volume or trends differ from previous time periods.",
        subTitle2:
            "Find insights across quality and compliance, too, to see how your team is performing.",
    },
    [SignalsDashboardType.ThemesAndTopics]: {
        enabled: false,
        title: "Themes and Topics Summary",
        subTitle1:
            "Themes give you visibility into what primary conversation drivers are arising naturally across your customer conversations.",
        subTitle2:
            "See what themes and topics are coming up most often, how they're trending over time, and how they relate to Eddy Effect prevalence.",
    },
    [SignalsDashboardType.ThemesAndDrivers]: {
        enabled: true,
        title: "Themes and Drivers",
        subTitle1:
            "Drivers represent the specific reasons customers contact you.\nThemes group related Drivers to reveal broader patterns.",
        subTitle2:
            "Drill into any Theme to see what's driving it - and use those insights to take action.",
    },
    [SignalsDashboardType.ContactTypes]: {
        enabled: false,
        title: "Contact Types",
        subTitle1:
            "Our Contact Type AI Model automatically identifies which personas are present across your interactions.",
        subTitle2:
            "Use this page to explore what types of contacts are chatting or calling in most often, and how Eddies are impacting them.",
    },
    [SignalsDashboardType.ModelResults]: {
        enabled: false,
        title: "Model Results",
        subTitle1:
            "Explore the results of AI models applied to your customer interactions.",
        subTitle2:
            "Use this page to analyze model performance and gain insights from the predictions.",
    },
    [SignalsDashboardType.Custom]: null,
    [SignalsDashboardType.Hidden]: null,
};

const StandardDashboardBanner: React.FC<Props> = ({ dashboardType }: Props) => {
    const copy = variantCopy[dashboardType];

    if (!copy || !copy.enabled) {
        return null;
    }

    const isEddy = dashboardType === SignalsDashboardType.Eddy;

    const rootStyle = {
        maxWidth: "none",
        height: "fit-content",
        backgroundColor: isEddy
            ? theme.palette.lightBlue.background
            : theme.palette.green.background,
        boxShadow: defaultShadowsForSignals,
        borderRadius: theme.shape.borderRadius * 2,
    };

    return (
        <Grid
            sx={{
                width: "100%",
                px: 2,
                mb: 2,
            }}
        >
            <AcxCard
                rootStyle={rootStyle}
                title={
                    <Typography
                        sx={{
                            textTransform: "uppercase",
                            letterSpacing: "2px",
                        }}
                        color={
                            isEddy
                                ? theme.palette.lightBlue.main
                                : theme.palette.green.main
                        }
                        fontWeight={700}
                    >
                        {copy.title}
                    </Typography>
                }
                mainContent={
                    <Grid container direction="column" gap={1}>
                        <Typography
                            variant="body1"
                            fontWeight={500}
                            sx={{ whiteSpace: "pre-line" }}
                        >
                            {copy.subTitle1}
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {copy.subTitle2}
                        </Typography>
                    </Grid>
                }
                mainContentStyle={{
                    paddingRight: theme.spacing(2.5),
                    paddingLeft: theme.spacing(2.5),
                    paddingTop: 0,
                    paddingBottom: theme.spacing(2),
                }}
                headerStyle={{
                    paddingRight: theme.spacing(2.5),
                    paddingLeft: theme.spacing(2.5),
                    paddingBottom: theme.spacing(0.5),
                }}
            />
        </Grid>
    );
};

export default StandardDashboardBanner;
